import { themeColors } from "./themeColors";
import i18n from "../i18n";

const dummyLogo = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' fill='%23006d77'%3E%3Cpath d='M256 512C397.4 512 512 397.4 512 256C512 114.6 397.4 0 256 0C114.6 0 0 114.6 0 256C0 397.4 114.6 512 256 512ZM216 112H296V192H376V272H296V352H216V272H136V192H216V112Z'/%3E%3C/svg%3E";

/**
 * Utility to parse and generate standardized headers across the application.
 * Driven by the 'prescriptionHeader' and 'selectHeaderTemplate' settings in AuthContext.
 */

// Helper to parse the prescriptionHeader string into structured data
export const parseHeader = (text) => {
    if (!text) return { hospitalName: '', doctors: [], otherInfo: '' };

    // Split lines and clean whitespace
    const lines = text.split('\n').map(l => l.trim()).filter(l => l !== '');
    const hospitalName = lines[0] || '';
    let doctors = [];
    let otherInfoIndex = 1;

    // Detect doctor names (starting with "Dr.")
    if (lines[1] && lines[1].toLowerCase().includes('dr.')) {
        doctors.push(lines[1]);
        otherInfoIndex = 2;
        if (lines[2] && lines[2].toLowerCase().includes('dr.')) {
            doctors.push(lines[2]);
            otherInfoIndex = 3;
        }
    } else {
        // Fallback for first line if no "Dr." prefix found in second line
        doctors.push(lines[1] || '');
        otherInfoIndex = 2;
    }

    return {
        hospitalName,
        doctors,
        otherInfo: lines.slice(otherInfoIndex).join(', ') || ''
    };
};

const hexToRgb = (hex) => {
    if (!hex) return [0, 0, 0];
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
};

const getContrastColor = (hex) => {
    if (!hex) return "#000000";
    const rgb = hexToRgb(hex);
    const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
};

// Generates HTML for the selected header template
export const getHeaderHtml = (hospitalData, isExport = false) => {
    const actualData = hospitalData?.hospitalData || hospitalData || {};
    const headerText = actualData.prescriptionHeader || '';
    const headerTemplateId = String(actualData.selectHeader || '1');
    const isPremiumHeader = (headerTemplateId === '1' || !['2', '3', '4', '5'].includes(headerTemplateId));
    const headerBgColor = actualData.headerBgColor || '#f0fdfa';
    const { hospitalName, doctors, otherInfo } = parseHeader(headerText);
    const themeName = actualData.applicationThemeColour || 'TEAL';
    const theme = themeColors[themeName] || themeColors.TEAL;

    // Bold Reg.No if found
    const formattedOtherInfo = otherInfo.replace(/Reg\.?No:?/gi, '<strong>Reg.No:</strong>');

    // If header is disabled in config, show empty space for print BUT show header for export
    if (actualData.PrescHeader === false && !isExport) return '<div style="height: 100px;"></div>';

    switch (headerTemplateId) {
        case '1': // Premium Compact Banner / Clean Header
            if (isExport) {
                // Premium Banner for Exported PDFs
                return `
                    <div style="background-color: ${theme.primary}; color: white; text-align: center; padding: 20px 10px; margin: -10px -10px 20px -10px; border-radius: 0; -webkit-print-color-adjust: exact;">
                        <h1 style="margin: 0; color: white; font-weight: 800; font-size: 24px; text-transform: uppercase;">${hospitalName}</h1>
                        <h3 style="margin: 5px 0; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 16px;">${doctors.filter(dr => dr).join(" | ")}</h3>
                        <p style="margin: 5px 0 0 0; color: rgba(255,255,255,0.8); font-size: 12px;">${formattedOtherInfo}</p>
                    </div>
                `;
            }
            return `
                <div style="background-color: white; color: black; text-align: center; padding: 15px 10px; margin-bottom: 10px; border-radius: 0;">
                    <h1 style="margin: 0; color: black; font-weight: 800; font-size: 24px; text-transform: uppercase;">${hospitalName}</h1>
                    <h2 style="margin: 5px 0; color: black; font-weight: 700; font-size: 18px;">${doctors.filter(dr => dr).join(" | ")}</h2>
                    <p style="margin: 5px 0 10px 0; color: black; font-size: 13px; font-weight: 500;">${formattedOtherInfo}</p>
                    <div style="border-top: 1.5px solid #333; width: 100%; margin: 10px auto;"></div>
                </div>
            `;
        case '2': // Classic Side-by-Side
            return `
                <div style="padding: 10px 0; margin-bottom: 15px; border-bottom: 2px solid black;">
                    <div style="text-align: center; margin-bottom: 10px;">
                        <h1 style="margin: 0; color: black; font-weight: 800; font-size: 22px; text-transform: uppercase;">${hospitalName}</h1>
                        <p style="margin: 5px 0 0 0; color: #333; font-size: 11px;">${formattedOtherInfo}</p>
                    </div>
                    <div style="display: flex; justify-content: space-between; border-top: 1px solid #eee; padding-top: 10px; margin-top: 5px;">
                        <div style="flex: 1; text-align: left;">
                            <h4 style="margin: 0; color: #444; font-weight: 700; font-size: 14px;">${doctors[0] || ''}</h4>
                        </div>
                        <div style="flex: 1; text-align: right;">
                            <h4 style="margin: 0; color: #444; font-weight: 700; font-size: 14px;">${doctors[1] || ''}</h4>
                        </div>
                    </div>
                </div>
            `;
        case '3': // Split Premium
            return `
                <div style="padding: 10px 0; margin-bottom: 15px; border: 2px solid black; background: #fff;">
                    <div style="display: flex; justify-content: space-between; align-items: center; padding: 0 10px;">
                        <div style="flex: 1;">
                            <h4 style="margin: 0; color: #444; font-weight: 700; font-size: 13px;">${doctors[0] || ''}</h4>
                        </div>
                        <div style="flex: 2; text-align: center;">
                            <h1 style="margin: 0; color: black; font-weight: 900; font-size: 20px; text-transform: uppercase;">${hospitalName}</h1>
                        </div>
                        <div style="flex: 1; text-align: right;">
                            <h4 style="margin: 0; color: #444; font-weight: 700; font-size: 13px;">${doctors[1] || ''}</h4>
                        </div>
                    </div>
                    <div style="text-align: center; margin-top: 10px; border-top: 1px dashed #ccc; padding-top: 5px;">
                        <p style="margin: 0; color: #666; font-size: 10px;">${formattedOtherInfo}</p>
                    </div>
                </div>
            `;
        case '4': // Modern with Logo
            return `
                <div style="display: flex; align-items: center; gap: 20px; padding: 15px; margin-bottom: 15px; border: 1px solid #eee; border-radius: 8px; background: white;">
                    <div style="flex: 0 0 80px;">
                        <img src="${dummyLogo}" style="width: 80px; height: 80px;" alt="logo" />
                    </div>
                    <div style="flex: 1;">
                        <h1 style="margin: 0; color: ${theme.primary}; font-weight: 800; font-size: 22px; text-transform: uppercase;">${hospitalName}</h1>
                        <h4 style="margin: 5px 0; color: #333; font-weight: 700; font-size: 15px;">${doctors.filter(dr => dr).join(" | ")}</h4>
                        <p style="margin: 5px 0 0 0; color: #666; font-size: 11px; border-top: 1px solid #f0f0f0; padding-top: 5px;">${formattedOtherInfo}</p>
                    </div>
                </div>
            `;
        case '5': // Custom Background Color
            const contrastColor = getContrastColor(headerBgColor);
            const secondaryTextColor = contrastColor === "#ffffff" ? "rgba(255,255,255,0.85)" : "#444";
            return `
                <div style="background-color: ${headerBgColor}; padding: 25px 15px; margin: 0 !important; margin-bottom: 15px !important; text-align: center; border-radius: 0; color: ${contrastColor}; print-color-adjust: exact; -webkit-print-color-adjust: exact; -moz-print-color-adjust: exact; color-adjust: exact;">
                    <h1 style="margin: 0 !important; color: ${contrastColor}; font-weight: 900; font-size: 26px; text-transform: uppercase;">${hospitalName}</h1>
                    <div style="display: flex; justify-content: center; gap: 40px; margin: 10px 0 !important;">
                        <div>
                            <h4 style="margin: 0 !important; font-size: 15px; font-weight: 700; color: ${contrastColor};">${doctors[0] || ''}</h4>
                        </div>
                        ${doctors[1] ? `
                        <div>
                            <h4 style="margin: 0 !important; font-size: 15px; font-weight: 700; color: ${contrastColor};">${doctors[1]}</h4>
                        </div>` : ''}
                    </div>
                    <p style="margin: 10px auto 0 !important; color: ${secondaryTextColor}; font-size: 12px; border-top: 1px solid ${contrastColor === "#ffffff" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)"}; padding-top: 10px; max-width: 600px;">${formattedOtherInfo}</p>
                </div>
            `;
        default:
            return `
                <div style="text-align: center; margin-bottom: 20px;">
                    <h1 style="margin: 0; font-size: 22px;">${hospitalName}</h1>
                    <h3 style="margin: 5px 0;">${doctors[0] || ''}</h3>
                    <p style="margin: 0;">${formattedOtherInfo}</p>
                </div>
            `;
    }
};

// Draw standardized header on a jsPDF instance
export const drawReportHeader = (doc, hospitalData, reportTitle, options = {}) => {
    const actualData = hospitalData?.hospitalData || hospitalData || {};
    const headerText = actualData.prescriptionHeader || '';
    const headerTemplateId = String(actualData.selectHeader || '1');
    const isPremiumHeader = (headerTemplateId === '1' || !['2', '3', '4', '5'].includes(headerTemplateId));
    const headerBgColor = actualData.headerBgColor || '#f0fdfa';
    const { hospitalName, doctors, otherInfo } = parseHeader(headerText);

    const pageWidth = doc.internal.pageSize.getWidth();
    const isPt = doc.internal.scaleFactor === 1;

    const defaultMargin = isPremiumHeader ? 0 : (isPt ? 40 : 15);
    const defaultTop = isPremiumHeader ? 0 : (isPt ? 40 : 15);

    const margin = options.margin !== undefined ? options.margin : defaultMargin;
    const contentMargin = options.contentMargin !== undefined ? options.contentMargin : (isPt ? 40 : 15);
    let currentY = options.top !== undefined ? options.top : defaultTop;

    const themeName = actualData.applicationThemeColour || 'TEAL';
    const theme = themeColors[themeName] || themeColors.TEAL;
    const primaryRgb = hexToRgb(theme.primary);

    if (actualData.PrescHeader === false && !options.isExport) {
        if (reportTitle) {
            doc.setFontSize(isPt ? 18 : 14);
            doc.setFont("helvetica", "bold");
            doc.text(reportTitle.toUpperCase(), pageWidth / 2, currentY, { align: "center" });
            return currentY + (isPt ? 50 : 20);
        }
        return currentY + (isPt ? 30 : 15);
    }

    doc.setTextColor(0, 0, 0);

    // Template 1: Premium Compact Banner
    if (headerTemplateId === '1' || !['2', '3', '4', '5'].includes(headerTemplateId)) {
        const headerHeight = isPt ? 95 : 32;
        doc.setFillColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
        doc.rect(margin, currentY, pageWidth - (margin * 2), headerHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(isPt ? 22 : 18);
        doc.setFont("helvetica", "bold");
        doc.text(hospitalName.toUpperCase(), pageWidth / 2, currentY + (isPt ? 35 : 12), { align: "center" });
        const drText = doctors.filter(dr => dr).join(" | ");
        doc.setFontSize(isPt ? 14 : 11);
        doc.text(drText, pageWidth / 2, currentY + (isPt ? 60 : 20), { align: "center" });
        doc.setFontSize(isPt ? 11 : 9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(230, 230, 230);
        doc.text(otherInfo, pageWidth / 2, currentY + (isPt ? 82 : 28), { align: "center" });
        currentY += headerHeight + (isPt ? 20 : 8);
    }
    // Template 2: Side-by-Side
    else if (headerTemplateId === '2') {
        const topOffset = isPt ? 25 : 8;
        doc.setFontSize(isPt ? 18 : 13);
        doc.setFont("helvetica", "bold");
        doc.text(hospitalName.toUpperCase(), pageWidth / 2, currentY + topOffset, { align: "center" });
        currentY += topOffset + (isPt ? 12 : 5);
        doc.setFontSize(isPt ? 9 : 7);
        doc.setFont("helvetica", "normal");
        doc.text(otherInfo, pageWidth / 2, currentY, { align: "center" });
        currentY += isPt ? 20 : 8;
        doc.setFontSize(isPt ? 11 : 9);
        doc.setFont("helvetica", "bold");
        doc.text(doctors[0] || '', contentMargin, currentY);
        doc.text(doctors[1] || '', pageWidth - contentMargin, currentY, { align: "right" });
        currentY += isPt ? 15 : 6;
    }
    // Template 3: Split Block
    else if (headerTemplateId === '3') {
        const headerHeightPT = isPt ? 70 : 22;
        doc.setFillColor(250, 250, 250);
        doc.rect(margin, currentY - (isPt ? 10 : 3), pageWidth - (margin * 2), headerHeightPT, 'F');
        doc.setLineWidth(isPt ? 1 : 0.3);
        doc.rect(margin, currentY - (isPt ? 10 : 3), pageWidth - (margin * 2), headerHeightPT, 'D');
        doc.setFontSize(isPt ? 10 : 8);
        doc.setFont("helvetica", "bold");
        doc.text(doctors[0] || '', contentMargin + (isPt ? 10 : 3), currentY + (isPt ? 10 : 3));
        doc.text(doctors[1] || '', pageWidth - contentMargin - (isPt ? 10 : 3), currentY + (isPt ? 10 : 3), { align: "right" });
        doc.setFontSize(isPt ? 18 : 13);
        doc.text(hospitalName.toUpperCase(), pageWidth / 2, currentY + (isPt ? 10 : 3), { align: "center" });
        currentY += isPt ? 30 : 10;
        doc.setFontSize(isPt ? 9 : 7);
        doc.setFont("helvetica", "normal");
        doc.text(otherInfo, pageWidth / 2, currentY, { align: "center" });
        currentY += isPt ? 35 : 12;
    }
    // Template 4: Modern with Logo
    else if (headerTemplateId === '4') {
        const headerHeight = (isPt ? 85 : 28);
        const logoSize = (isPt ? 60 : 20);
        try {
            doc.addImage(dummyLogo, 'PNG', margin + (isPt ? 10 : 3), currentY + (isPt ? 12 : 4), logoSize, logoSize);
        } catch (e) {}
        const textLeft = margin + logoSize + (isPt ? 30 : 10);
        doc.setFontSize(isPt ? 18 : 13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
        doc.text(hospitalName.toUpperCase(), textLeft, currentY + (isPt ? 30 : 10));
        doc.setFontSize(isPt ? 11 : 9);
        doc.setTextColor(0, 0, 0);
        doc.text(doctors.filter(d => d).join(" | "), textLeft, currentY + (isPt ? 50 : 17));
        doc.setFontSize(isPt ? 9 : 7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100, 100, 100);
        doc.text(otherInfo, textLeft, currentY + (isPt ? 68 : 23));
        currentY += headerHeight + (isPt ? 20 : 8);
    }
    // Template 5: Custom Background
    else if (headerTemplateId === '5') {
        const headerHeight = (isPt ? 95 : 32);
        const bgColorRgb = hexToRgb(headerBgColor);
        const contrastColor = getContrastColor(headerBgColor);
        const contrastRgb = hexToRgb(contrastColor);
        
        doc.setFillColor(bgColorRgb[0], bgColorRgb[1], bgColorRgb[2]);
        doc.rect(margin, currentY, pageWidth - (margin * 2), headerHeight, 'F');
        
        doc.setTextColor(contrastRgb[0], contrastRgb[1], contrastRgb[2]);
        doc.setFontSize(isPt ? 20 : 15);
        doc.setFont("helvetica", "bold");
        doc.text(hospitalName.toUpperCase(), pageWidth / 2, currentY + (isPt ? 35 : 12), { align: "center" });

        doc.setFontSize(isPt ? 12 : 9);
        doc.text(doctors.filter(d => d).join(" | "), pageWidth / 2, currentY + (isPt ? 60 : 20), { align: "center" });

        doc.setFontSize(isPt ? 10 : 8);
        doc.setFont("helvetica", "normal");
        const secondaryContrastColor = contrastColor === "#ffffff" ? [220, 220, 220] : [60, 60, 60];
        doc.setTextColor(secondaryContrastColor[0], secondaryContrastColor[1], secondaryContrastColor[2]);
        doc.text(otherInfo, pageWidth / 2, currentY + (isPt ? 82 : 28), { align: "center" });
        
        currentY += headerHeight + (isPt ? 20 : 8);
    }

    doc.setTextColor(0, 0, 0);
    if (!options.hideSeparator && !isPremiumHeader) {
        doc.setLineWidth(isPt ? 0.5 : 0.2);
        doc.line(contentMargin, currentY, pageWidth - contentMargin, currentY);
        currentY += isPt ? 25 : 8;
    } else {
        currentY += isPt ? 20 : 8;
    }

    if (reportTitle) {
        doc.setFontSize(isPt ? 15 : 11);
        doc.setFont("helvetica", "bold");
        doc.text(reportTitle.toUpperCase(), pageWidth / 2, currentY, { align: "center" });
        currentY += isPt ? 25 : 10;
    }

    return currentY;
};

export const drawAuthorizedSignatory = (doc, hospitalData, label = "AUTHORIZED SIGNATORY") => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const isPt = doc.internal.scaleFactor === 1;
    const actualData = hospitalData?.hospitalData || hospitalData || {};
    const hospitalName = actualData.hospitalName || "";
    const margin = isPt ? 40 : 15;
    const sigWidth = isPt ? 180 : 60;
    const startX = pageWidth - margin - sigWidth;
    const startY = pageHeight - (isPt ? 80 : 25);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(isPt ? 0.8 : 0.3);
    doc.line(startX, startY, pageWidth - margin, startY);
    doc.setFontSize(isPt ? 11 : 8);
    doc.setFont("helvetica", "bold");
    doc.text(label.toUpperCase(), startX + sigWidth / 2, startY + (isPt ? 15 : 5), { align: "center" });
    doc.setFontSize(isPt ? 10 : 7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(hospitalName, startX + sigWidth / 2, startY + (isPt ? 30 : 10), { align: "center" });
};
